import { NewCheckoutI } from "../api/request/Lead/Interfaces/DataInterface.response";

export interface PriceTupleV3 {
  real: number;
  promoted: number;
}

export interface PricesV3 {
  monthly?: PriceTupleV3;
  yearly?: PriceTupleV3;
}

/**
 * `/api/pricing/capitals` computes `yearly.promoted` as the sum of the 12
 * discounted monthly prices (PromotedPremiumDecorator::yearlyDiscountedPrice).
 * That equals the annual fee in ES (annual = 12 × monthly), but not in IT:
 * the AGL annual premium is cheaper than 12 months and the annual fee actually
 * charged is `annual − 12 × monthly discount` (YearlyFeeDtoPremiumVisitor).
 * Example (10_sconto): monthly 49,96 → 44,96; annual 580 → fee 520, endpoint 539,52.
 *
 * Until the backend exposes the annual fee, derive it here so the annual card
 * shows what will be charged. Only applied to markets whose annual premium is
 * not 12 × monthly (see PaymentPage.controller).
 */
export const withAnnualFeePromoted = (prices?: PricesV3): PricesV3 | undefined => {
  const monthly = prices?.monthly;
  const yearly = prices?.yearly;
  if (!monthly || !yearly) return prices;
  const monthlyDiscount = monthly.real - monthly.promoted;
  if (!(monthlyDiscount > 0)) return prices;
  const promoted = Math.max(
    0,
    Math.round((yearly.real - 12 * monthlyDiscount) * 100) / 100
  );
  return { ...prices, yearly: { ...yearly, promoted } };
};

/**
 * Amount the checkout will actually charge for the SELECTED period. The
 * payment payload is re-fetched on every period change, so this is the source
 * of truth for the active card and the total; `prices` (both frequencies at
 * once) is what the non-selected card relies on.
 */
export const chargedPriceForSelectedPeriod = (
  checkout: Pick<NewCheckoutI, "isPromoted" | "priceToPay">
): number | undefined =>
  checkout.isPromoted && checkout.priceToPay > 0 ? checkout.priceToPay : undefined;
